import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "../lib/api";
import { Brand } from "../components/Brand";
import { ScoreGauge } from "../components/ScoreGauge";
import { ShieldCheck, Download, Share2, Loader2, Building2, Info } from "lucide-react";
import { toast } from "sonner";

const scoreColor = (v) => (v >= 85 ? "#10B981" : v >= 70 ? "#0055FF" : v >= 50 ? "#F59E0B" : "#EF4444");

export default function AgencyScorecard() {
  const { vid } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(false);

  useEffect(() => {
    api.get(`/agency/scorecard/${vid}`).then((r) => setData(r.data)).catch(() => setErr(true));
  }, [vid]);

  const share = async () => {
    const url = window.location.href;
    try {
      if (navigator.share) await navigator.share({ title: "Einstein Score™ Credential", url });
      else { await navigator.clipboard.writeText(url); toast.success("Link copied to clipboard"); }
    } catch (e) { console.debug("Share cancelled or unavailable", e); }
  };
  const downloadQR = () => { const a = document.createElement("a"); a.href = data.qr; a.download = `einstein-${vid}.png`; a.click(); };

  if (err) return (
    <div className="min-h-screen grid place-items-center bg-background px-5">
      <div className="text-center"><div className="font-heading text-2xl font-bold text-ink">Credential not found</div>
        <Link to="/" className="text-brand font-semibold text-sm mt-2 inline-block">← Back to home</Link></div>
    </div>
  );
  if (!data) return <div className="min-h-screen grid place-items-center bg-background"><Loader2 className="h-8 w-8 animate-spin text-brand" /></div>;

  const dims = data.dimensions || {};
  const labels = data.competency_labels || {};

  return (
    <div className="min-h-screen bg-background" data-testid="agency-public-scorecard">
      <header className="glass border-b border-slate-200 px-5 h-16 flex items-center justify-between sticky top-0 z-10">
        <Brand />
        <div className="flex gap-2">
          <button onClick={downloadQR} data-testid="agency-download-qr" className="inline-flex items-center gap-1.5 text-xs font-bold text-ink bg-slate-100 hover:bg-slate-200 px-3.5 py-2 rounded-full transition-colors"><Download className="h-3.5 w-3.5" /> QR</button>
          <button onClick={share} data-testid="agency-share-scorecard" className="inline-flex items-center gap-1.5 text-xs font-bold text-white bg-brand hover:bg-brand-hover px-3.5 py-2 rounded-full transition-colors"><Share2 className="h-3.5 w-3.5" /> Share</button>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-5 py-10 fade-up">
        <div className="rounded-3xl border border-slate-200 bg-white shadow-lift overflow-hidden">
          <div className="relative text-white p-8 overflow-hidden" style={{ background: "#0f172a" }}>
            <div className="absolute inset-0 bg-grid opacity-20" />
            <div className="relative">
              <div className="inline-flex items-center gap-2 text-xs font-bold text-emerald-300 bg-emerald-500/15 px-3 py-1.5 rounded-full mb-3">
                <ShieldCheck className="h-3.5 w-3.5" /> Verified Einstein Score™ Credential
              </div>
              <h1 className="font-heading text-2xl sm:text-3xl font-extrabold tracking-tight">{data.candidate_name}</h1>
              <p className="text-slate-200 text-sm mt-1 max-w-md">Verified candidate readiness for <b>{data.target_role_name || "target role"}</b>. Presented by a Mock2Crack recruitment partner.</p>
              <div className="mt-6 flex flex-wrap items-end justify-between gap-4">
                <Brand light />
                {data.agency?.name && (
                  <div className="flex items-center gap-2" data-testid="agency-scorecard-brand">
                    {data.agency.logo ? <img src={data.agency.logo} alt="Agency" className="h-11 w-11 object-contain rounded-lg bg-white/90 p-1" /> : <span className="h-11 w-11 rounded-lg bg-white/15 grid place-items-center"><Building2 className="h-6 w-6 text-white" /></span>}
                    <span className="text-sm font-bold text-white">{data.agency.name}</span>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="p-8 grid md:grid-cols-3 gap-8">
            <div className="md:col-span-2 space-y-6">
              <div className="grid sm:grid-cols-2 gap-y-4 gap-x-8">
                {[["Candidate", data.candidate_name], ["Target Role", data.target_role_name || "—"],
                  ["Presented To", data.client_name || "General"], ["Published", data.published_at],
                  ["Verification ID", data.verification_id], ["Readiness", data.readiness_level?.label]].map(([k, v]) => (
                  <div key={k}><div className="text-[11px] font-bold uppercase tracking-wide text-ink-faint">{k}</div><div className="font-semibold text-ink num">{v}</div></div>
                ))}
              </div>
              {Object.keys(dims).length > 0 && (
                <div>
                  <div className="font-heading font-bold text-ink mb-3">Assessed Dimensions</div>
                  <div className="space-y-2.5">
                    {Object.entries(dims).map(([k, val]) => (
                      <div key={k} className="flex items-center gap-3"><div className="w-32 text-sm text-ink-soft shrink-0">{labels[k] || k}</div>
                        <div className="flex-1 h-2.5 rounded-full bg-slate-100 overflow-hidden"><div className="h-full rounded-full" style={{ width: `${val}%`, background: scoreColor(val) }} /></div>
                        <div className="num font-bold text-sm text-ink w-8 text-right">{val}</div></div>
                    ))}
                  </div>
                </div>
              )}
              <p className="text-[11px] text-ink-faint flex items-start gap-1.5 pt-2 border-t border-slate-100"><Info className="h-3.5 w-3.5 shrink-0 mt-0.5" /> Einstein Score™ is Mock2Crack's proprietary interview-readiness indicator based on assessed competencies and job-role fit. It is a readiness signal, not a hiring guarantee.</p>
            </div>

            <div className="space-y-6">
              <div className="rounded-2xl border border-slate-200 p-5 flex flex-col items-center">
                <div className="text-[11px] font-bold uppercase tracking-wide text-ink-faint mb-2">Einstein Score™</div>
                <ScoreGauge value={data.score} size={140} />
                <span className="mt-3 text-xs font-bold px-3 py-1 rounded-full" style={{ background: `${data.readiness_level?.color}22`, color: data.readiness_level?.color }}>{data.readiness_level?.label}</span>
              </div>
              <div className="rounded-2xl border border-slate-200 p-5 text-center">
                <img src={data.qr} alt="QR" className="mx-auto h-32 w-32" data-testid="agency-scorecard-qr" />
                <div className="text-[11px] text-ink-faint mt-2">Scan to verify this credential</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
